import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Slider, Typography, Card, CardContent, Stack } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import demoAudio from '../../assets/sample.mp3';
const AudioPlayer = ({ audioFile = demoAudio, title = "Demo Audio Track" }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const setAudioTime = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = (e) => {
      console.error("Audio loading error:", e);
      setError("Could not load audio file. Please check the file and try again.");
      setIsLoading(false);
    };

    // event listeners
    audio.addEventListener('loadeddata', setAudioData);
    audio.addEventListener('timeupdate', setAudioTime);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Set initial volume
    audio.volume = volume;

    // Cleanup
    return () => {
      audio.removeEventListener('loadeddata', setAudioData);
      audio.removeEventListener('timeupdate', setAudioTime);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioFile]);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch(error => {
            console.error("Playback failed:", error);
            setError("Playback failed. This might be due to browser autoplay policies or an issue with the audio file.");
          });
      }
    }
  };

  const handleTimeChange = (_, newValue) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const handleVolumeChange = (_, newValue) => {
    if (audioRef.current) {
      const newVolume = newValue / 100;
      audioRef.current.volume = newVolume;
      setVolume(newVolume);
      if (newVolume === 0) {
        setIsMuted(true);
      } else {
        setIsMuted(false);
      }
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Reset player
  const resetPlayer = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      if (!isPlaying) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            setIsPlaying(true);
          }).catch(err => console.error(err));
        }
      }
    }
  };

  // Format time in minutes:seconds
  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  return (
    <Card 
      elevation={3} 
      sx={{ 
        maxWidth: 900, 
        width: '100%', 
        bgcolor: 'background.paper',
        borderRadius: 4,
        overflow: 'hidden',
        margin: 'auto'
      }}
    >
      <Box
        sx={{
          height: 120,
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          position: 'relative',
          backgroundImage: 'primary.main',
          p: 2,
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" fontWeight="bold" >
          {title}
        </Typography>
      </Box>
      
      <CardContent>
        <audio 
          ref={audioRef} 
          src={audioFile} 
          preload="auto" 
        />
        
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
            {error}
          </Typography>
        )}
        
        {isLoading ? (
          <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
            Loading audio...
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              {/* Time slider */}
              <Box sx={{ width: '100%', mb: 1 }}>
                <Slider
                  value={currentTime}
                  max={duration || 0}
                  onChange={handleTimeChange}
                  size="small"
                  color="primary"
                  disabled={!!error}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatTime(currentTime)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatTime(duration)}
                  </Typography>
                </Box>
              </Box>
              
              <Stack 
                direction="row" 
                spacing={1} 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 2
                }}
              >
                {/* Left controls group - play and reset */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton 
                    onClick={togglePlay} 
                    color="primary" 
                    disabled={!!error}
                    sx={{ mr: 1 }}
                  >
                    {isPlaying ? 
                      <PauseIcon sx={{ fontSize: 38 }} /> : 
                      <PlayArrowIcon sx={{ fontSize: 38 }} />
                    }
                  </IconButton>
                  
                  <IconButton 
                    onClick={resetPlayer} 
                    color="primary" 
                    disabled={!!error}
                  >
                    <RestartAltIcon sx={{ fontSize: 24 }} />
                  </IconButton>
                </Box>
                
                {/* Right controls group - volume */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton onClick={toggleMute} disabled={!!error}>
                    {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
                  </IconButton>
                  
                  <Slider
                    value={isMuted ? 0 : volume * 100}
                    onChange={handleVolumeChange}
                    size="small"
                    sx={{ 
                      width: 100,
                      '& .MuiSlider-thumb': {
                        width: 12,
                        height: 12,
                      }
                    }}
                    disabled={!!error}
                  />
                </Stack>
              </Stack>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AudioPlayer;